import { Link } from 'react-router-dom';
import { List as Menu, SignOut as LogOut, Checks } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import Ico3, { PointsMark } from '../Ico3';
import BrandLogo from '../Brand';
import { avatarSrc } from '../../lib/avatars';
import { needsTelegramBind, profileName } from '../../lib/user';
import api, { apiError } from '../../lib/api';
import { useSettings } from '../../context/SettingsContext';
import { activeSocials, socialLink, socialIconSrc, socialHandle } from '../../lib/socials';

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const NotificationBell = ({ user }) => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const box = useRef(null);

  const loadCount = () => api.get('/me/notifications', { params: { limit: 1 } })
    .then((r) => setUnread(r.data.unread || 0))
    .catch(() => {});

  const loadList = () => api.get('/me/notifications', { params: { limit: 20 } })
    .then((r) => {
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    })
    .catch(() => setItems([]));

  useEffect(() => {
    if (!user) {
      setUnread(0);
      setItems(null);
      setOpen(false);
      return undefined;
    }
    loadCount();
    const t = window.setInterval(loadCount, 25000);
    return () => window.clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (open && user) loadList();
  }, [open, user]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markOne = async (id) => {
    try {
      await api.post(`/me/notifications/${id}/read`);
      setItems((list) => (list || []).map((n) => (n.id === id ? { ...n, read: true } : n)));
      setUnread((n) => Math.max(0, n - 1));
    } catch (e) { toast.error(apiError(e)); }
  };

  const markAll = async () => {
    try {
      await api.post('/me/notifications/read-all');
      setItems((list) => (list || []).map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        data-testid="header-notifications"
        onClick={() => setOpen((v) => !v)}
        className="relative h-10 w-10 rounded-full btn-ghost flex items-center justify-center"
        title="Bildirimler"
        aria-label="Bildirimler"
        aria-expanded={open}
      >
        <Ico3 name="bell" className="w-6 h-6" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[1.05rem] h-[1.05rem] px-1 rounded-full bg-bh-crimson text-[9px] font-bold text-white flex items-center justify-center leading-none">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="header-notifications-panel"
          className="absolute right-0 top-[calc(100%+0.4rem)] z-50 w-[min(22rem,calc(100%-1.5rem))] rounded-2xl border border-white/10 bg-bh-surface shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/5">
            <p className="font-display font-extrabold text-sm">Bildirimler</p>
            {unread > 0 && (
              <button type="button" onClick={markAll} className="text-[11px] font-bold text-bh-accent hover:underline inline-flex items-center gap-1" data-testid="header-notifications-read-all">
                <Checks className="w-3.5 h-3.5" /> Tümünü oku
              </button>
            )}
          </div>
          <div className="max-h-[min(24rem,70vh)] overflow-y-auto">
            {items === null ? (
              <p className="px-4 py-8 text-center text-xs text-gray-500">Yükleniyor...</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-xs text-gray-500" data-testid="header-notifications-empty">Henüz bildirim yok.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => !n.read && markOne(n.id)}
                  className={`w-full text-left px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.03] ${n.read ? 'opacity-60' : ''}`}
                  data-testid={`header-notification-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-display font-bold text-xs">{n.title}</p>
                    <span className="text-[10px] text-gray-500 shrink-0">{fmtDate(n.created_at)}</span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 whitespace-pre-wrap line-clamp-3">{n.body}</p>
                  {!n.read && <span className="inline-block mt-1.5 text-[10px] font-bold uppercase text-bh-accent">Yeni</span>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Header = ({ onMenu }) => {
  const { user, logout, openAuth, openTelegramBind } = useAuth();
  const { site } = useSettings();
  const socials = activeSocials(site);


  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-bh-canvas/90 border-b border-white/5 pt-[env(safe-area-inset-top)]">
      <div className="h-14 sm:h-16 px-2 sm:px-6 flex items-center gap-1.5 sm:gap-3 min-w-0 overflow-hidden">
        <button onClick={onMenu} data-testid="mobile-menu-toggle" className="lg:hidden w-11 h-11 rounded-lg btn-ghost flex items-center justify-center shrink-0" aria-label="Menü">
          <Menu className="w-5 h-5" />
        </button>

        <Link to="/" className="lg:hidden flex items-center shrink-0 min-w-0 overflow-hidden" data-testid="header-logo-link">
          <div className="neon-always rounded-lg border border-white/10 bg-bh-surface/70 px-2 py-1 flex items-center justify-center overflow-hidden">
            <BrandLogo className="h-5 sm:h-6 w-auto max-w-[72px] sm:max-w-[108px] object-contain" />
          </div>
        </Link>

        <div className="flex flex-1 min-w-0 items-center self-stretch px-1 sm:px-2 lg:px-4" data-testid="header-socials">
          {socials.length > 0 ? (
            <div className="home-socials header-socials w-full">
              {socials.map((s, i) => (
                <a
                  key={`${s.kind}-${i}`}
                  href={socialLink(s)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={socialHandle(s)}
                  data-testid={`header-social-${s.kind}`}
                  className={`home-social-card is-${s.kind}`}
                >
                  <img src={socialIconSrc(s.kind)} alt="" className="home-social-ico" />
                  <span className="home-social-handle">{socialHandle(s)}</span>
                </a>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 ml-auto shrink-0">
          <Link to="/cark" data-testid="header-wheel-cta" className="hidden md:inline-flex h-10 items-center gap-1.5 px-4 rounded-full btn-ghost text-xs font-bold">
            <img src="/icons3d/wheel.png" alt="" className="w-5 h-5 object-contain" /> Çark
          </Link>

          {user ? (
            <>
              {needsTelegramBind(user) && (
                <button
                  type="button"
                  data-testid="header-telegram-bind"
                  onClick={openTelegramBind}
                  className="h-10 inline-flex items-center px-3 sm:px-4 rounded-lg btn-accent text-xs whitespace-nowrap"
                >
                  Telegram bağla
                </button>
              )}
              <NotificationBell user={user} />
              <Link to="/profil" data-testid="user-points-display" className="flex items-center h-10 max-w-[7.5rem] sm:max-w-none pl-1.5 pr-2.5 sm:pl-2 sm:pr-3.5 rounded-lg bg-bh-surface border border-white/10 overflow-hidden">
                <PointsMark value={user.points || 0} size="w-5 h-5 sm:w-6 sm:h-6" className="text-xs sm:text-sm" />
              </Link>
              <Link to="/profil" data-testid="user-name-display" className="hidden sm:flex items-center gap-2 h-10 px-2.5 sm:px-3 rounded-lg bg-bh-surface border border-white/10 hover:border-white/20 transition-colors">
                <img src={avatarSrc(user.avatar)} alt="" className="w-7 h-7 rounded-lg object-contain shrink-0" />
                <span className="text-xs font-medium truncate max-w-[100px]">{profileName(user)}</span>
              </Link>
              <button
                data-testid="header-logout-btn"
                onClick={async () => { await logout(); toast.info('Çıkış yapıldı.'); }}
                className="h-10 w-10 rounded-full btn-ghost flex items-center justify-center text-gray-400 hover:text-bh-crimson"
                title="Çıkış Yap"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button data-testid="header-login-btn" onClick={openAuth} className="hidden sm:inline-flex h-10 items-center px-4 rounded-full btn-ghost text-xs font-bold">
                Giriş Yap
              </button>
              <button data-testid="header-register-btn" onClick={openAuth} className="h-10 inline-flex items-center px-3 sm:px-5 rounded-full btn-accent text-xs whitespace-nowrap">
                <span className="sm:hidden">Giriş</span>
                <span className="hidden sm:inline">Hemen Kaydol</span>
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
